import { Manager as SocketManager } from "socket.io-client";
import { debugLogMajorEvent } from "../../DebugLogger";
import urlStatusChecker from "./ThrottledURLStatusChecker";
import { metricsManager as globalsMetricsManager } from "../../Globals";
import { Logger } from "../../Logger";
import { VideoAuth } from "./VideoAuth";
import { tracker } from "../../public/tracker/Tracker";
import { sleep } from "gather-common/dist/src/public/promises";
import { GVCEventEmitter } from "../../EventEmitter/GVCEventEmitter";
import { WebSocketConnectionState, WebSocketDisconnectReason, } from "../../EventEmitter/WebSocketEvents";
import { reasonToStatusCode } from "./socketIOUtils";
const CONNECT_ERROR_RETRY_MS = 2000;
const SEND_WITH_RESPONSE_DEFAULT_TIMEOUT_MS = 15000;
export class Signaling extends GVCEventEmitter {
    _ws;
    streamId = "";
    serverURL = "";
    _serverHealthCheckURL;
    pendingResponses = new Set();
    _connectOrDisconnectTime;
    _destroyed = false;
    emitListener = [];
    _sequenceNumber = 0;
    constructor(serverURL, streamId, userId, options) {
        super();
        this.serverURL = serverURL;
        this._serverHealthCheckURL = `${this.serverURL.replace(/^ws/, "http")}/healthCheck`;
        this.streamId = streamId;
        const videoAuth = new VideoAuth(userId);
        const socketPath = this.getWebsocketPathFromURL(serverURL);
        const socketManager = new SocketManager(serverURL, {
            autoConnect: false,
            reconnection: false,
            transports: ["websocket"],
            path: socketPath,
            query: {
                ...options,
            },
        });
        this._ws = socketManager.socket("/", {
            auth: (callback) => {
                callback({
                    token: videoAuth.latestToken(),
                    streamId: this.streamId,
                });
            },
        });
        this._ws.on("connect", () => {
            debugLogMajorEvent(`WebSocket to ${serverURL} connected, connection id = ${this._ws.id}`);
            this._connectOrDisconnectTime = Date.now();
            this.publishWebSocketConnectionStateChange(WebSocketConnectionState.Connected, {
                serverURL: this.serverURL,
            });
        });
        this._ws.on("connect_error", async (error) => {
            if (error.message !== "bad auth") {
                if (process.env.ENVIRONMENT !== "local") {
                    Logger.warn(`${this.serverURL} connect error:`, error);
                    globalsMetricsManager.addMetricReliably("gvc-connect-error", {
                        serverURL: this.serverURL,
                        error: error.name,
                        message: error.message,
                    });
                    urlStatusChecker.check(this._serverHealthCheckURL);
                }
                this.onDisconnect(error.message);
                return;
            }
            Logger.warn("Signaling: bad auth");
            globalsMetricsManager.addMetricReliably("gvc-bad-auth");
            const waitForToken = await videoAuth.waitForToken();
            await Promise.all([waitForToken, sleep(CONNECT_ERROR_RETRY_MS)]);
            if (!this._destroyed) {
                this._ws.connect();
            }
        });
        this._ws.on("disconnect", this.onDisconnect);
        videoAuth.waitForToken().then((_token) => {
            socketManager.open();
            this._ws.open();
        });
    }
    onDisconnect = (reason) => {
        const reasonCode = reasonToStatusCode(reason);
        if (this._connectOrDisconnectTime || reasonCode === WebSocketDisconnectReason.Unknown) {
            debugLogMajorEvent(`WebSocket to ${this.serverURL} disconnected, reason = ${reason}, connection id = ${this._ws.id}`);
            globalsMetricsManager.addMetricReliably("ws_disconnect", {
                sfuAddr: this.serverURL,
                reason,
            });
        }
        this._connectOrDisconnectTime = Date.now();
        this.publishWebSocketConnectionStateChange(WebSocketConnectionState.Disconnected, {
            serverURL: this.serverURL,
            reason: reasonToStatusCode(reason),
        });
    };
    destroy() {
        this._destroyed = true;
        this._ws.off("disconnect", this.onDisconnect);
        this._ws.disconnect();
        this.pendingResponses.forEach(({ reject, rejectTimeout }) => {
            clearTimeout(rejectTimeout);
            reject();
        });
    }
    async sendWithResponse(event, data, timeoutMS = SEND_WITH_RESPONSE_DEFAULT_TIMEOUT_MS) {
        return new Promise((resolve, reject) => {
            if (this._destroyed) {
                reject(`Signaling destroyed, cannot send ${event}`);
                return;
            }
            let rejectTimeout = window.setTimeout(() => {
                rejectTimeout = null;
                this.pendingResponses.delete(pendingResponse);
                const timeString = this._connectOrDisconnectTime
                    ? `${((Date.now() - this._connectOrDisconnectTime) / 1000).toFixed(2)}s ago`
                    : "construction";
                reject(`sendWithResponse for ${event} timed out. Socket to ${this.serverURL} has been ${this._ws.connected ? "connected" : "disconnected"} since ${timeString}`);
            }, timeoutMS);
            const pendingResponse = {
                reject: () => reject(`Signaling instance destroyed before a response for ${event} was received`),
                rejectTimeout,
            };
            this.pendingResponses.add(pendingResponse);
            this.emitListener.forEach((listener) => listener(event, data));
            if (event !== "addrs" && event !== "reassign" && event !== "get-addr") {
                ++this._sequenceNumber;
                data = { wsSequenceNumber: this._sequenceNumber, zodData: data };
            }
            this._ws.emit(event, data, (response) => {
                if (rejectTimeout != null) {
                    clearTimeout(rejectTimeout);
                    rejectTimeout = null;
                    this.pendingResponses.delete(pendingResponse);
                    tracker.captureSFUMessage(this.serverURL, "incoming", `${event} response`, response);
                    resolve(response);
                }
                else {
                    Logger.error(`Received a late response for ${event}, promise was already rejected`);
                }
            });
        });
    }
    emit(event, data) {
        if (event !== "unsubscribe" && event !== "debug-router" && event !== "remote-log") {
            ++this._sequenceNumber;
            data = { wsSequenceNumber: this._sequenceNumber, zodData: data };
        }
        this._ws.emit(event, data);
        this.emitListener.forEach((listener) => listener(event, data));
    }
    on(event, cb) {
        this._ws.on(event, cb);
    }
    onAnyIncoming(cb) {
        this._ws.onAny(cb);
    }
    onAnyOutgoing(cb) {
        this.emitListener.push(cb);
    }
    _summarize() {
        return { connected: this._ws.connected };
    }
    getConnectionReport() {
        const { pingTimeout, pingInterval } = this._ws.io.engine || {};
        return {
            connected: this._ws.connected,
            connectOrDisconnectTime: this._connectOrDisconnectTime ?? 0,
            pingTimeout,
            pingInterval,
        };
    }
    getWebsocketPathFromURL = (serverURL) => {
        const url = new URL(serverURL);
        const path = url.pathname;
        const newPath = [path, "socket.io"].join("/");
        const socketPath = newPath.replace(/\/\//, "/");
        return socketPath;
    };
}
//# sourceMappingURL=Signaling.js.map